import React from 'react';
import { Navbar } from '../../components/Navbar';
import { Footer } from '../../components/Footer';
import { Link } from 'react-router-dom';
import { ArrowRight, Wrench, Shield, TrendingUp, CheckCircle, Car, Zap, FileText, Globe } from 'lucide-react';

const TechnicalIndustrial = () => {
    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-950">
            <Navbar />
            <main className="pt-24 pb-16">
                {/* Hero Section */}
                <section className="py-20 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-orange-500/10 to-amber-500/10 blur-3xl" />
                    <div className="container mx-auto px-4 relative z-10">
                        <div className="max-w-4xl mx-auto text-center">
                            <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-full px-4 py-2 mb-6">
                                <Car className="w-5 h-5 text-orange-400" />
                                <span className="text-orange-300 font-medium text-sm">Skilled Worker Recruitment Program</span>
                            </div>
                            <h1 className="text-5xl md:text-7xl font-black text-white mb-6">
                                Automotive Technician <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-500">Jobs in Germany</span>
                            </h1>
                            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                                Germany has a strong and growing demand for skilled Automotive Technicians (Kfz-Mechatroniker). Through the German Skilled Worker Immigration Program, qualified international candidates can legally work and settle in Germany with full employment benefits and long-term residency options.
                            </p>
                            <Link
                                to="/signup"
                                className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-[0_0_30px_rgba(249,115,22,0.5)] transition-all"
                            >
                                Apply Now
                                <ArrowRight className="w-5 h-5" />
                            </Link>
                        </div>
                    </div>
                </section>

                {/* Job Profile & Responsibilities */}
                <section className="py-16">
                    <div className="container mx-auto px-4">
                        <h2 className="text-3xl md:text-5xl font-black text-white text-center mb-12">
                            Job Profile & Responsibilities
                        </h2>
                        <div className="max-w-5xl mx-auto mb-10 text-center">
                            <p className="text-slate-300 text-lg">
                                Automotive Technicians in Germany are responsible for the inspection, diagnosis, maintenance, and repair of vehicles using modern mechanical and electronic systems.
                            </p>
                        </div>
                        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                            <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700 rounded-2xl p-8">
                                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                                    <Wrench className="w-6 h-6 text-orange-400" /> Typical Work Areas
                                </h3>
                                <ul className="space-y-4">
                                    {[
                                        'Passenger cars and vans',
                                        'Trucks and commercial vehicles',
                                        'Authorized brand service centers',
                                        'Fleet maintenance workshops',
                                        'Hybrid and electric vehicle service centers'
                                    ].map((item, i) => (
                                        <li key={i} className="flex items-start gap-3 text-slate-300">
                                            <div className="w-1.5 h-1.5 rounded-full bg-orange-400 mt-2 shrink-0" />
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700 rounded-2xl p-8">
                                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                                    <Zap className="w-6 h-6 text-orange-400" /> Key Responsibilities
                                </h3>
                                <ul className="space-y-4">
                                    {[
                                        'Engine and transmission maintenance',
                                        'Brake, suspension, and steering systems',
                                        'Electrical and electronic diagnostics',
                                        'Use of OBD and computerized diagnostic tools',
                                        'Preventive maintenance and inspections',
                                        'Compliance with German safety and quality standards'
                                    ].map((item, i) => (
                                        <li key={i} className="flex items-start gap-3 text-slate-300">
                                            <div className="w-1.5 h-1.5 rounded-full bg-orange-400 mt-2 shrink-0" />
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Eligibility Criteria */}
                <section className="py-16 bg-slate-800/30">
                    <div className="container mx-auto px-4">
                        <h2 className="text-3xl md:text-5xl font-black text-white text-center mb-12">
                            Eligibility Criteria
                        </h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
                            <div className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700 hover:border-orange-500/50 transition-all">
                                <div className="text-4xl mb-4">🎓</div>
                                <h3 className="text-xl font-bold text-white mb-3">Education</h3>
                                <ul className="space-y-2 text-slate-400 text-sm">
                                    <li>• ITI – Mechanic Motor Vehicle</li>
                                    <li>• Diploma in Automobile Engineering</li>
                                    <li>• Vocational Tech Course</li>
                                    <li>• B.E/B.Tech (with workshop exp.)</li>
                                    <li><em className="text-xs text-slate-500 mt-2 block">*From recognized inst.</em></li>
                                </ul>
                            </div>
                            <div className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700 hover:border-orange-500/50 transition-all">
                                <div className="text-4xl mb-4">🧰</div>
                                <h3 className="text-xl font-bold text-white mb-3">Work Experience</h3>
                                <ul className="space-y-2 text-slate-400 text-sm">
                                    <li>• Min. 1-3 years practical workshop experience</li>
                                    <li>• Vehicle repair & diagnostics mandatory</li>
                                    <li>• Experience with modern diagnostic equipment is an advantage</li>
                                </ul>
                            </div>
                            <div className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700 hover:border-orange-500/50 transition-all">
                                <div className="text-4xl mb-4">🗣</div>
                                <h3 className="text-xl font-bold text-white mb-3">German Language</h3>
                                <ul className="space-y-2 text-slate-400 text-sm">
                                    <li>• Minimum A2 level German required</li>
                                    <li>• B1 level highly recommended for faster integration and career growth</li>
                                </ul>
                            </div>
                            <div className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700 hover:border-orange-500/50 transition-all">
                                <div className="text-4xl mb-4">🧑‍⚕</div>
                                <h3 className="text-xl font-bold text-white mb-3">Medical & Police</h3>
                                <ul className="space-y-2 text-slate-400 text-sm">
                                    <li>• Medical fitness certificate required</li>
                                    <li>• Police Clearance Certificate (PCC) required</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Recruitment Process & Docs */}
                <section className="py-16">
                    <div className="container mx-auto px-4 max-w-5xl">
                        <div className="grid md:grid-cols-2 gap-12">
                            <div>
                                <h2 className="text-3xl font-black text-white mb-8">
                                    Recruitment Process
                                </h2>
                                <p className="text-slate-400 mb-6">Our recruitment process follows legal and transparent procedures in coordination with German employers and authorities.</p>
                                <div className="space-y-6">
                                    {[
                                        { title: 'Step 1: Profile Evaluation', desc: 'We evaluate candidate qualifications, experience, and language level to ensure suitability.' },
                                        { title: 'Step 2: Employer Interview', desc: 'Shortlisted candidates attend online interviews with German workshops. May include technical discussions.' },
                                        { title: 'Step 3: Job Offer & Contract', desc: 'Selected candidates receive an official contract, salary details, and a training plan if required.' },
                                    ].map((step, i) => (
                                        <div key={i} className="flex gap-4">
                                            <div className="w-8 h-8 shrink-0 bg-orange-500/20 text-orange-400 rounded-full flex items-center justify-center font-bold">
                                                {i + 1}
                                            </div>
                                            <div>
                                                <h4 className="text-white font-bold">{step.title}</h4>
                                                <p className="text-slate-400 text-sm">{step.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <h2 className="text-3xl font-black text-white mb-8 flex items-center gap-3">
                                    Required Documents
                                </h2>
                                <div className="bg-slate-800/50 p-8 rounded-2xl border border-slate-700">
                                    <ul className="space-y-3">
                                        {[
                                            'Valid passport & Passport-size photos',
                                            'Updated professional CV',
                                            'Educational certificates',
                                            'Work experience certificates',
                                            'German language certificate',
                                            'Police Clearance Certificate (PCC)',
                                            'Medical fitness report'
                                        ].map((doc, i) => (
                                            <li key={i} className="flex items-center gap-3 text-slate-300">
                                                <FileText className="w-5 h-5 text-orange-400 shrink-0" />
                                                <span>{doc}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Why Germany */}
                <section className="py-16 bg-slate-800/30">
                    <div className="container mx-auto px-4">
                        <h2 className="text-3xl md:text-5xl font-black text-white text-center mb-12">
                            Why Choose Germany for Automotive Techs
                        </h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
                            {[
                                'Strong automotive industry (BMW, VW, Audi, Mercedes, etc.)',
                                'High demand and job security',
                                'Advanced technology and training exposure',
                                'Excellent labor protection laws',
                                'Free public schooling for children',
                                'Long-term settlement opportunities',
                                'Career growth in EV and automation sectors'
                            ].map((reason, i) => (
                                <div key={i} className="bg-slate-800/50 p-4 rounded-xl border border-slate-700 flex items-center gap-3">
                                    <CheckCircle className="w-5 h-5 text-green-400 shrink-0" />
                                    <span className="text-slate-300 font-medium">{reason}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="py-16">
                    <div className="container mx-auto px-4">
                        <div className="max-w-3xl mx-auto text-center bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-500/30 rounded-3xl p-12">
                            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
                                Ready to Accelerated Your Career?
                            </h2>
                            <p className="text-slate-300 mb-8">
                                Connect with verified employers and get complete support from candidate screening to visa and post-arrival assistance.
                            </p>
                            <Link
                                to="/signup"
                                className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-[0_0_30px_rgba(249,115,22,0.5)] transition-all"
                            >
                                Start Application
                                <ArrowRight className="w-5 h-5" />
                            </Link>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
};

export default TechnicalIndustrial;
